import { Link, createFileRoute } from '@tanstack/react-router';
import { ExternalLink, LogOut } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Field } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { SettingsSection } from '@/components/settings-section';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { auth } from '@/lib/auth';
import { ROLE_LABELS, useClients, useIsDemo, useMe, useUpdateMe } from '@/lib/clients';
import { formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/settings/profile')({ component: ProfileAndSecurity });

/** Field errors for the profile form (the API repeats them). */
export function profileErrors(form: { name: string; phone: string }): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!form.name.trim()) errors['name'] = 'Enter your name';
  else if (form.name.trim().length > 120) errors['name'] = 'At most 120 characters';
  if (form.phone.trim().length > 40) errors['phone'] = 'At most 40 characters';
  return errors;
}

/** The sign-in provider as people know it: its host name. */
export function providerName(issuer: string | undefined): string {
  if (!issuer) return 'your sign-in provider';
  try {
    return new URL(issuer).host;
  } catch {
    return issuer;
  }
}

interface Session {
  signedInAt: string | null;
  expiresAt: string | null;
}

/** Profile and security (document 06 G14): name and phone, the sign-in, and the clients open to me. */
function ProfileAndSecurity() {
  const me = useMe();
  const isDemo = useIsDemo();
  const clients = useClients();
  const update = useUpdateMe();
  const [form, setForm] = useState({ name: '', phone: '' });
  const [session, setSession] = useState<Session>({ signedInAt: null, expiresAt: null });
  const issuer = auth.manager?.settings.authority;

  useEffect(() => {
    let live = true;
    void auth.getUser().then((u) => {
      if (!live || !u) return;
      const at = typeof u.profile.auth_time === 'number' ? u.profile.auth_time : null;
      setSession({
        signedInAt: at ? new Date(at * 1000).toISOString() : null,
        expiresAt: u.expires_at ? new Date(u.expires_at * 1000).toISOString() : null,
      });
    });
    return () => {
      live = false;
    };
  }, []);

  if (me.isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (me.isError || !me.data)
    return (
      <>
        <PageHeader title="Profile and security" />
        <EmptyState
          title="Could not load your profile"
          hint={me.isError ? me.error.message : 'Sign in to see your profile.'}
        />
      </>
    );
  const user = me.data;
  const staffRoles = user.roles.filter((r) => r in ROLE_LABELS);

  return (
    <>
      <PageHeader
        title="Profile and security"
        description={
          isDemo
            ? 'The demo visitor’s profile is read-only. Create your own account to change yours.'
            : 'Your name and phone as colleagues and clients see them, and how you sign in.'
        }
      />
      <div className="grid gap-4 xl:grid-cols-2">
        <SettingsSection
          title="Profile"
          testId="profile-section"
          canEdit={!isDemo}
          rows={[
            ['Name', user.name],
            ['Email', user.email],
            ['Phone', user.phone ?? ''],
            [
              'Role',
              staffRoles.length ? (
                <span className="flex flex-wrap gap-1">
                  {staffRoles.map((r) => (
                    <Badge key={r} variant="secondary">
                      {ROLE_LABELS[r]}
                    </Badge>
                  ))}
                </span>
              ) : (
                'Client user'
              ),
            ],
          ]}
          onEdit={() => setForm({ name: user.name, phone: user.phone ?? '' })}
          onSave={async () => {
            const errors = profileErrors(form);
            if (Object.keys(errors).length) return errors;
            await update.mutateAsync({ name: form.name.trim(), phone: form.phone.trim() || null });
            return undefined;
          }}
        >
          {(errors) => (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" error={errors['name']}>
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={Boolean(errors['name'])}
                    value={form.name}
                    maxLength={120}
                    autoComplete="name"
                    onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Phone" error={errors['phone']} hint="Optional">
                {(id, describedBy) => (
                  <Input
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={Boolean(errors['phone'])}
                    value={form.phone}
                    maxLength={40}
                    autoComplete="tel"
                    inputMode="tel"
                    onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                  />
                )}
              </Field>
              <Field label="Email" hint="Comes from your sign-in and cannot be changed here.">
                {(id) => <Input id={id} value={user.email} readOnly disabled />}
              </Field>
            </div>
          )}
        </SettingsSection>

        <section className="rounded-lg border bg-card" data-testid="security-section">
          <div className="px-5 pt-4">
            <h2 className="text-base font-semibold">Sign-in and security</h2>
          </div>
          <dl className="grid grid-cols-[220px_1fr] gap-x-6 gap-y-2 px-5 pt-3 pb-4 text-sm">
            <dt className="text-muted-foreground">Signed in as</dt>
            <dd>{user.email}</dd>
            <dt className="text-muted-foreground">Sign-in provider</dt>
            <dd>{auth.enabled ? providerName(issuer) : 'None (sign-in is off)'}</dd>
            <dt className="text-muted-foreground">Signed in</dt>
            <dd>{session.signedInAt ? formatDateTime(session.signedInAt) : '—'}</dd>
            <dt className="text-muted-foreground">Session renews until</dt>
            <dd>{session.expiresAt ? formatDateTime(session.expiresAt) : '—'}</dd>
            <dt className="text-muted-foreground">Password and two-step verification</dt>
            <dd>
              {auth.accountUrl
                ? `Managed on ${providerName(issuer)}.`
                : `Managed by ${providerName(issuer)}; ask your administrator to change them.`}
            </dd>
          </dl>
          <div className="flex flex-wrap gap-2 px-5 pb-5">
            {auth.accountUrl ? (
              <Button asChild variant="outline" className="gap-2">
                <a href={auth.accountUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-4" /> Change password or two-step verification
                </a>
              </Button>
            ) : null}
            {auth.enabled ? (
              <Button
                type="button"
                variant="outline"
                className="gap-2"
                onClick={() => void auth.signOut()}
              >
                <LogOut className="size-4" /> Sign out
              </Button>
            ) : null}
          </div>
        </section>

        <section className="rounded-lg border bg-card xl:col-span-2" data-testid="access-section">
          <div className="flex items-start justify-between gap-4 px-5 pt-4">
            <div>
              <h2 className="text-base font-semibold">Your access</h2>
              <p className="text-sm text-muted-foreground">
                The clients you can open and what you can do in each.
              </p>
            </div>
            <Link to="/settings/notifications" className="text-sm text-primary">
              Notifications
            </Link>
          </div>
          {clients.isPending ? (
            <p className="px-5 pb-4 text-sm text-muted-foreground">Loading…</p>
          ) : !clients.data?.length ? (
            <p className="px-5 pb-4 text-sm text-muted-foreground">
              No clients yet. Ask your accountant to add you.
            </p>
          ) : (
            <Table className="mt-2">
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Accountants</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.data.map((c) => (
                  <TableRow key={c.id} data-testid="access-row">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell>{ROLE_LABELS[c.role] ?? c.role}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {c.accountants.join(', ') || '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </section>
      </div>
    </>
  );
}
